import { Application } from "pixi.js";
import { ConfigParams } from "../GameConfig";
import { SceneManager } from "../scenes/SceneManager";
import GlobalStore from "./GlobalStore";
import { WebSocketClient } from "./WebSocketClient";
import { WebSocketMessageHandler } from "./WebSocketMessageHandler";
import Scene from "../scenes/Scene";
import { JackpotHitData } from "../models/JackpotHitData";
import { GameEvents, MessageEvents } from "../events/EventEnums";
import { ParametersMessage } from "../models/Messages";
import { CurrentJackpotValue } from "../models/JackpotCurValue";
import Keyboard from "./Keyboard";

export default class SignController {
  private static instance: SignController | null = null;

  private readonly wsClient;
  private readonly wsHandler;
  private readonly sceneManager;

  static init(
    wsClient: WebSocketClient,
    app: Application,
    config: ConfigParams,
  ): SignController {
    if (SignController.instance) {
      throw new Error("SignController is already initialized");
    }
    SignController.instance = new SignController(wsClient, app, config);

    return SignController.instance;
  }

  public static getInstance(): SignController {
    if (!SignController.instance) {
      throw new Error(
        "SignController not initialized. Call SignController.init(...) first.",
      );
    }

    return SignController.instance;
  }

  private constructor(
    wsClient: WebSocketClient,
    app: Application,
    config: ConfigParams,
  ) {
    this.wsClient = wsClient;
    this.wsHandler = new WebSocketMessageHandler(wsClient);
    this.sceneManager = SceneManager(app, config);

    wsClient.onMessage = this.wsHandler.handle;

    this.registerEventListeners();

    const newHitKey = Keyboard("n");
    newHitKey.press = () => {
      console.log("NEW HIT");
      this.jackpotHit({
        jackpot: 10,
        machineId: 0,
        name: "TEST",
        timestamp: new Date(),
        value: 1000 + Math.round(Math.random() * 3000),
        location: "TEST LOCATION",
      });
    };
  }

  // TODO: Improve emitter handling to avoid as any
  private registerEventListeners = (): void => {
    GlobalStore.messageEventEmitter.on(
      MessageEvents.MESSAGE_PARAMETERS,
      this.updateParameters as any,
    );
    GlobalStore.messageEventEmitter.on(
      MessageEvents.MESSAGE_JACKPOT_HIT,
      this.jackpotHit as any,
    );
    GlobalStore.messageEventEmitter.on(
      MessageEvents.MESSAGE_JACKPOT_HISTORY,
      this.updateJackpotHistory as any,
    );
    GlobalStore.messageEventEmitter.on(
      MessageEvents.MESSAGE_PROGRESSIVE_CUR_VALUES,
      this.updateCurValues as any,
    );
  };

  private updateParameters = (msg: ParametersMessage): void => {
    console.log("UPDATE PARAMETERS", msg);
    GlobalStore.clearActiveJackpots();
    if (msg.values === undefined || msg.values.length === 0) return;
    for (const jp of msg.values) {
      GlobalStore.addActiveJackpotId(jp.jp);
    }
  };

  private jackpotHit = (hit: JackpotHitData): void => {
    console.log("JACKPOT HIT", hit);
    GlobalStore.gameEventEmitter.emit(GameEvents.JACKPOT_HIT, hit);
  };

  private updateJackpotHistory = (newHistory: JackpotHitData[]): void => {
    console.log("UPDATE JACKPOT HISTORY", newHistory);

    const history = GlobalStore.getJackpotHistory().filter((item) => {
      return GlobalStore.isJackpotActive(item.jackpot);
    });

    history
      .sort((a, b) => {
        return b.timestamp.getTime() - a.timestamp.getTime();
      })
      .splice(0, history.length);

    for (const el of newHistory) {
      history.push(el);
    }

    // this.sceneHistory.updateHistory(history);
    GlobalStore.setJackpotHistory(history);
    GlobalStore.gameEventEmitter.emit(GameEvents.JACKPOT_HISTORY_UPDATED);
  };

  private updateCurValues = (curValues: CurrentJackpotValue[]): void => {
    for (const val of curValues) {
      GlobalStore.setCurJackpot(val.code, val);
    }
    GlobalStore.gameEventEmitter.emit(GameEvents.JACKPOT_CUR_VALUES_UPDATED);
  };

  public registerScene = (
    scene: Scene,
    isJackpotHit: boolean = false,
  ): void => {
    scene.hide();
    this.sceneManager.addScene(scene);
    if (isJackpotHit) this.sceneManager.setJackpotHitScene(scene);
  };
}
